import { OcChatConfig } from '../../../types/oc';
import { getCookie } from '../../../utils/misc';

/**
 * OC API Client - Generic HTTP client for OC API requests
 */

/**
 * HTTP client configuration
 */
export interface OcApiClientConfig {
  baseUrl: string;
  headers: Record<string, string>;
}

/**
 * Build base API URL for OC API
 * Returns the base URL at the API version level
 * Controller names are included in individual endpoint paths
 * Allows override via config for development/testing
 */
export const buildOcApiBaseUrl = (config?: OcChatConfig): string => {
  return config?.apiBaseUrl || 'https://api.connectedcommunity.org/api/v2.0';
};

/**
 * Check if authentication headers are available
 * Returns true if either iamKey is provided OR both HLTenantKey and HLAuthToken cookies exist
 */
export const hasAuthHeaders = (config: OcChatConfig): boolean => {
  // If iamKey is provided, we have authentication
  if (config.iamKey && config.iamKey.trim() !== '') {
    return true;
  }

  // Otherwise, check if both required cookies are available
  const tenantKey = getCookie('HLTenantKey');
  const authToken = getCookie('HLAuthToken');
  return tenantKey !== null && authToken !== null;
};

/**
 * Get request headers for OC API calls
 * Uses either HLIAMKey header (if iamKey provided) or cookie-based authentication
 * Cookie-based auth uses HLTenantKey and HLAuthToken headers from cookies
 */
export const getOcHeaders = (config: OcChatConfig): Record<string, string> => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Use iamKey if provided
  if (config.iamKey) {
    headers['HLIAMKey'] = config.iamKey;
  } else {
    // Otherwise, use cookie-based authentication
    const tenantKey = getCookie('HLTenantKey');
    const authToken = getCookie('HLAuthToken');

    if (tenantKey) {
      headers['HLTenantKey'] = tenantKey;
    }
    if (authToken) {
      headers['HLAuthToken'] = authToken;
    }
  }

  if (config.HL_Test) {
    headers['HL_Test'] = config.HL_Test;
  }

  return headers;
};

/**
 * Create an OC API client instance
 */
export const createOcApiClient = (config: OcChatConfig): OcApiClientConfig => {
  return {
    baseUrl: buildOcApiBaseUrl(config),
    headers: getOcHeaders(config),
  };
};

/**
 * Generic GET request
 */
export const get = async <T>(
  client: OcApiClientConfig,
  path: string,
): Promise<T> => {
  const url = `${client.baseUrl}${path}`;

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: client.headers,
  });

  if (!response.ok) {
    // Handle authentication/authorization errors
    if (response.status === 401 || response.status === 403) {
      throw new Error('Unauthorized - User not authenticated');
    }
    throw new Error(`GET ${path} failed: ${response.statusText}`);
  }

  const data: T = await response.json();
  return data;
};

/**
 * Generic POST request
 */
export const post = async <T, D = unknown>(
  client: OcApiClientConfig,
  path: string,
  data?: D,
): Promise<T> => {
  const url = `${client.baseUrl}${path}`;

  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: client.headers,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    // Handle authentication/authorization errors
    if (response.status === 401 || response.status === 403) {
      throw new Error('Unauthorized - User not authenticated');
    }
    throw new Error(`POST ${path} failed: ${response.statusText}`);
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  const responseData: T = JSON.parse(text);
  return responseData;
};

/**
 * Generic PUT request
 */
export const put = async <T, D = unknown>(
  client: OcApiClientConfig,
  path: string,
  data?: D,
): Promise<T> => {
  const url = `${client.baseUrl}${path}`;

  const response = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: client.headers,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  });

  if (!response.ok) {
    // Handle authentication/authorization errors
    if (response.status === 401 || response.status === 403) {
      throw new Error('Unauthorized - User not authenticated');
    }
    throw new Error(`PUT ${path} failed: ${response.statusText}`);
  }

  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  const responseData: T = JSON.parse(text);
  return responseData;
};

/**
 * Generic DELETE request
 */
export const del = async <T>(
  client: OcApiClientConfig,
  path: string,
): Promise<T> => {
  const url = `${client.baseUrl}${path}`;

  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
    headers: client.headers,
  });

  if (!response.ok) {
    // Handle authentication/authorization errors
    if (response.status === 401 || response.status === 403) {
      throw new Error('Unauthorized - User not authenticated');
    }
    throw new Error(`DELETE ${path} failed: ${response.statusText}`);
  }

  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  const responseData: T = JSON.parse(text);
  return responseData;
};

/**
 * Generic OPTIONS request
 */
export const options = async <T>(
  client: OcApiClientConfig,
  path: string,
): Promise<T> => {
  const url = `${client.baseUrl}${path}`;

  const response = await fetch(url, {
    method: 'OPTIONS',
    credentials: 'include',
    headers: client.headers,
  });

  if (!response.ok) {
    // Handle authentication/authorization errors
    if (response.status === 401 || response.status === 403) {
      throw new Error('Unauthorized - User not authenticated');
    }
    throw new Error(`OPTIONS ${path} failed: ${response.statusText}`);
  }

  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  const responseData: T = JSON.parse(text);
  return responseData;
};


